import { ProductHelper } from "@core/helpers/product-helper";
import { AccountHistoricValue } from "@core/models/account-historic-values.model";
import { AccountRiskProfile } from "@core/models/account-risk-profile.model";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { DisbursementWithTax } from "@core/models/disbursement-with-tax.model";
import { AccountFund, Fund } from "@core/models/fund.model";
import { FundRatioHistory } from "@core/models/fund-ratio-history.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { GeneralAccountCorrection } from "@core/models/general-account-correction.model";
import { GroupedGfhTransaction } from "@core/models/grouped-gfh-transaction.model";
import { Participations } from "@core/models/participations.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TAllClients } from "@core/models/user.model";
import { CompanyUser } from "@core/models/user-company.model";
import { Utilities } from "@core/utils/utils";
import { TPagination } from "@custom-types/sorting";
import { ExtendedGfhAccount } from "@models/extended-gfh-account.model";
import { InternalTransfer } from "@models/internal-transfer.model";
import { Quotation } from "@models/quotation.model";
import { PrivateUser } from "@models/user-private.model";
import { WithdrawalAssignment } from "@models/withdrawal-assignment.model";
import { createFeatureSelector, createSelector } from "@ngrx/store";

import * as fromClientsAndAccounts from "./clients-and-accounts.reducers";

export const SELECT_CLIENTS_AND_ACCOUNTS_STATE =
  createFeatureSelector<fromClientsAndAccounts.ClientsAndAccountsState>(
    fromClientsAndAccounts.CLIENTS_AND_ACCOUNTS_FEATURE_KEY
  );

export const SELECT_ALL_CLIENTS_PAGINATED = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<TAllClients> => {
    return state.allClientsPaginated;
  }
);

export const SELECT_ALL_CLIENTS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<TAllClients> => {
    return state.allClients;
  }
);

export const SELECT_PRIVATE_CLIENTS_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<PrivateUser> => {
    return state.privateClients;
  }
);

export const SELECT_COMPANY_CLIENTS_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<CompanyUser> => {
    return state.companyClients;
  }
);

export const SELECT_CLIENTS_AND_ACCOUNTS_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.loading
);

export const SELECT_CLIENT_ACCOUNT_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<ExtendedGfhAccount> => {
    return state.accountList;
  }
);

export const SELECT_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.accountsLoading
);

export const SELECT_CLIENT_BSN_VALIDITY = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.valid
);

export const SELECT_ACTIVE_ACCOUNT = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): ExtendedGfhAccount =>
    state.activeAccount
);

export const SELECT_ACCOUNT_TRANSACTIONS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<GroupedGfhTransaction> => state.transactions
);

export const SELECT_CORRECTION_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<GeneralAccountCorrection> => state.correctionList
);

export const SELECT_CORRECTION_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.correctionLoading
);

export const SELECT_ACCOUNT_HISTORIC_VALUE_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<AccountHistoricValue> => state.accountHistoricValueList
);

export const SELECT_FUND_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): Array<Fund> =>
    ProductHelper.getProductFunds(state.activeAccount.productVersion.product)
);

export const SELECT_FUND_LIST_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.fundListLoading
);

export const SELECT_PARTICIPATION_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<Participations> => state.activeAccount.participations
);

export const SELECT_LATEST_NAV_FUND_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<FundWithRelated> => state.fundList
);

export const SELECT_SHOW_ACCOUNT_HISTORIC_VALUES_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.accountConfiguration.showHistoricFundValues
);

export const SELECT_DYNAMIC_PRODUCT_FUND_RELATION_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.accountConfiguration.dynamicProductFundRelation
);

export const SELECT_WITHDRAWAL_ASSIGNMENT_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<WithdrawalAssignment> => state.withdrawalAssignmentList
);

export const SELECT_ACCOUNT_TRANSACTIONS_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.transactionsLoading
);

export const SELECT_LATEST_NAV_FUND_LIST_LENGTH = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): number =>
    state.fundList.length
);

export const SELECT_PARTICIPATION_LIST_LENGTH = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): number =>
    state.activeAccount.participations.length
);

export const SELECT_ACCOUNT_TRANSACTIONS_LENGTH = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): number =>
    state.transactions.length
);

export const SELECT_ACCOUNT_DISBURSEMENTS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<DisbursementWithTax> => state.disbursements
);

export const SELECT_ACCOUNT_DISBURSEMENTS_LOADING_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.disbursementsLoading
);

export const SELECT_INTERNAL_TRANSFER_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<InternalTransfer> => state.internalTransferList
);

export const SELECT_PAUSE_REASON_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.pauseReasonStatus
);

export const SELECT_ACCOUNT_NEEDS_IDENTIFICATION_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.accountConfiguration.accountNeedsIdentification
);

export const SELECT_ACCOUNT_ALLOW_TRANSFER_FUND_AMOUNT_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.accountConfiguration.allowTransferFundAmount
);

export const SELECT_QUOTATION_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): Array<Quotation> => {
    return state.quotationList;
  }
);

export const SELECT_RISK_PROFILES = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<RiskProfile | CompositionRiskProfile> => {
    return state.riskProfiles;
  }
);

export const SELECT_COMPOSITION_FUND_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<CompositionVersion> => {
    return state.compositionFundList;
  }
);

export const SELECT_IMPERSONATION_ACCOUNT_REQUEST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): string => {
    return state.impersonationAccountRequest;
  }
);

export const SELECT_ACCOUNT_RISK_PROFILES = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<AccountRiskProfile> => {
    return state.accountRiskProfiles;
  }
);

export const SELECT_FUND_RATIO_HISTORY = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Array<FundRatioHistory> => {
    return state.fundRatioHistory;
  }
);

export const SELECT_TRADE_EXTERNALLY_STATUS = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean => {
    return state.accountConfiguration.tradeExternally;
  }
);

export const SELECT_EMAIL_VALIDITY = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.emailExists
);

export const SELECT_CLIENT_ACCOUNTS_LIST_LENGTH = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): number =>
    state.clientListLength
);

export const SELECT_CLIENT_ACCOUNTS_PAGINATION = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (
    state: fromClientsAndAccounts.ClientsAndAccountsState
  ): Partial<TPagination> | undefined => state.pagination
);

export const SLECT_CLIENT_ACCOUNTS_PAGE_INDEX = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): number =>
    Utilities.getPageIndex(state.pagination?.pageIndex)
);

export const SELECT_ACCOUNT_FUND_LIST = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): Array<AccountFund> =>
    state.accountFundList
);

export const SELECT_LEI_NUMBER_VALIDITY = createSelector(
  SELECT_CLIENTS_AND_ACCOUNTS_STATE,
  (state: fromClientsAndAccounts.ClientsAndAccountsState): boolean =>
    state.leiExists
);
